import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import {
    useAccount,
    useMsalStore,
} from '@precon-tools-suite-by-shared/vue-msal';
import { getAccountByPolicy } from '../helpers/accountSelection';
import type { AccountInfo } from '@azure/msal-common';
import { getB2CTenantName } from '../helpers/environmentInfo';
import { Maybe } from '@precon-tools-suite-by-shared/js-utils';
import { useNormalizedUserName } from '@precon-tools-suite-by-shared/shared-auth-ui';
import {
    type TokenClaims,
    userIdFromClaims,
    teamIdFromClaims,
    emailFromClaims,
} from '../helpers/interpret-claims';

export const useAuthenticationStore = defineStore(
    'precon-authentication',
    () => {
        const msalStore = useMsalStore();

        function accountByPolicy(policyId: string): AccountInfo | null {
            return getAccountByPolicy(msalStore.accounts, policyId);
        }

        const loginPolicy = ref<string>('B2C_1A_signup_signin');
        const profileEditPolicy = ref<string>('B2C_1A_ProfileEdit');
        const passwordResetPolicy = ref<string>('B2C_1A_PasswordReset');

        const loginAccount = useAccount(() =>
            accountByPolicy(loginPolicy.value)
        );
        const profileEditAccount = useAccount(() =>
            accountByPolicy(profileEditPolicy.value)
        );
        const passwordResetAccount = useAccount(() =>
            accountByPolicy(passwordResetPolicy.value)
        );

        const tenantName = computed<string | null>(() =>
            Maybe.fold(
                getB2CTenantName(loginAccount.accountInfo.value),
                getB2CTenantName(profileEditAccount.accountInfo.value),
                getB2CTenantName(passwordResetAccount.accountInfo.value)
            ).default(null)
        );

        const name = useNormalizedUserName(() => ({
            family_name:
                profileEditAccount.familyName.value ??
                loginAccount.familyName.value ??
                undefined,
            given_name:
                profileEditAccount.givenName.value ??
                loginAccount.givenName.value ??
                undefined,
            display_name:
                profileEditAccount.fullName.value ??
                loginAccount.fullName.value ??
                undefined,
        }));

        const accountInfo = computed(
            () =>
                loginAccount.accountInfo.value ??
                profileEditAccount.accountInfo.value ??
                passwordResetAccount.accountInfo.value ??
                null
        );

        const isAuthenticated = computed(() =>
            msalStore.accounts ? msalStore.accounts.length > 0 : false
        );

        const idTokenClaims = computed<TokenClaims>(
            () => accountInfo.value?.idTokenClaims ?? {}
        );
        const trustFrameworkPolicy = computed<string | null>(
            () => idTokenClaims.value.tfp ?? null
        );
        const identityProvider = computed<string | null>(
            () => identityProvider.value.idp ?? null
        );

        const userId = computed(() =>
            userIdFromClaims(idTokenClaims.value as TokenClaims)
        );
        const teamId = computed(() =>
            teamIdFromClaims(idTokenClaims.value as TokenClaims)
        );
        const email = computed(() =>
            emailFromClaims(idTokenClaims.value as TokenClaims)
        );

        const hasTeam = computed(() => teamId.value !== null);
        const hasUser = computed(() => userId.value !== null);

        const { msal, loading, accounts, interactionStatus } =
            storeToRefs(msalStore);

        return {
            msal,
            accounts,
            interactionStatus,
            accountByPolicy,
            loginPolicy,
            profileEditPolicy,
            passwordResetPolicy,
            tenantName,
            name,
            accountInfo,
            isAuthenticated,
            idTokenClaims,
            trustFrameworkPolicy,
            identityProvider,
            userId,
            teamId,
            email,
            hasTeam,
            hasUser,
            loading,
            tenantId: loginAccount.tenantId,
            subjectId: loginAccount.subjectId,
            environment: loginAccount.environment,
        };
    }
);
